import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, registerables, ChartConfiguration } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import html2canvas from 'html2canvas';
import 'chartjs-adapter-date-fns';

import {
  ICompanyThemeActivityPoint,
  IThematicInnovationMetricKey,
} from '../../../../../../core/models/results.model';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';

type MetricConfig = {
  label: string;
  color: string;
  yTitle: string;
  hoverSuffix?: string;
  decimals?: number;
  opacity?: number;
  footnote: string;
};

const FONT_FAMILY = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const GROWTH_METRIC: IThematicInnovationMetricKey = 'inv_growth_relative_12m_sia';

// "2026-08-31" -> "Aug 2026"
const formatMonthYear = (iso: string): string => {
  const d = new Date(iso);
  return `${d.toLocaleString('en', { month: 'short', timeZone: 'UTC' })} ${d.getUTCFullYear()}`;
};

const COLOR = {
  axisText: '#64748b',
  axisTitle: '#64748b',
  tooltipBg: '#ffffff',
  tooltipTitle: '#64748b',
  tooltipBody: '#0f172a',
  tooltipBorder: 'rgba(15, 23, 42, 0.08)',
  grid: 'rgba(15, 23, 42, 0.05)',
  zeroLine: 'rgba(15, 23, 42, 0.3)',
  chartAreaBg: '#f8fafc',
};

const METRIC_OPTIONS: Record<IThematicInnovationMetricKey, MetricConfig> = {
  inv_growth_absolute_12m: {
    label: 'Abs. Growth (Inventions, 12m)',
    color: '#a2c613',
    yTitle: 'Inventions (12m)',
    hoverSuffix: ' Inventions',
    footnote: 'the latest year uses a rolling last 12 months period',
  },
  inv_growth_relative_12m_sia: {
    label: 'Rel. Growth (% YoY, 12m)',
    color: '#f59e0b',
    yTitle: 'Relative Growth (%)',
    hoverSuffix: '%',
    decimals: 1,
    opacity: 0.45,
    footnote: 'the latest year uses the latest month for the calculation',
  },
  invention_count_120m: {
    label: 'Size (Cumulative, 10y)',
    color: '#22c55e',
    yTitle: 'Inventions (10y)',
    hoverSuffix: ' Inventions',
    footnote: 'the latest year uses a rolling last 120 months period',
  },
};

const whiteBackgroundPlugin = {
  id: 'companyThemeActivityWhiteBg',
  beforeDraw(chart: Chart) {
    const ctx = chart.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

const chartAreaBackgroundPlugin = {
  id: 'companyThemeActivityAreaBg',
  beforeDatasetsDraw(chart: Chart) {
    const { left, top, width, height } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.fillStyle = COLOR.chartAreaBg;
    ctx.fillRect(left, top, width, height);
    ctx.restore();
  },
};

const zeroLinePlugin = {
  id: 'companyThemeActivityZeroLine',
  afterDatasetsDraw(chart: Chart, _args: any, opts: { enabled?: boolean }) {
    if (!opts?.enabled) return;
    const yScale = chart.scales['y'];
    if (!yScale) return;
    const y = yScale.getPixelForValue(0);
    const { left, right } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(right, y);
    ctx.strokeStyle = COLOR.zeroLine;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  },
};

Chart.register(...registerables, zoomPlugin);

@Component({
  selector: 'app-company-theme-activity-graph',
  standalone: true,
  imports: [CommonModule, BaseChartDirective],
  templateUrl: './company-theme-activity-graph.component.html',
  styleUrl: './company-theme-activity-graph.component.css',
})
export class CompanyThemeActivityGraphComponent implements OnChanges {
  @Input() tabId!: string;
  @Input() entityid!: string;
  @Input() entity_name: string | null = null;
  @Input() theme_label: string | null = null;
  @Input() theme_search_type: 'cluster' | 'custom' = 'cluster';
  @Input() cluster_id: number | string | null = null;
  @Input() cluster_name: string | null = null;
  @Input() query: string | null = null;

  @ViewChild(BaseChartDirective) private chartDirective?: BaseChartDirective;
  @ViewChild('exportTarget') private exportTarget?: ElementRef<HTMLDivElement>;

  readonly metricButtons = (Object.keys(METRIC_OPTIONS) as IThematicInnovationMetricKey[]).map((key) => ({
    key,
    label: METRIC_OPTIONS[key].label,
    color: METRIC_OPTIONS[key].color,
  }));
  selectedMetric: IThematicInnovationMetricKey = 'inv_growth_absolute_12m';

  data: ICompanyThemeActivityPoint[] = [];
  chartjsData!: ChartConfiguration<'bar'>['data'];
  chartjsOptions!: ChartConfiguration<'bar'>['options'];
  readonly chartPlugins = [whiteBackgroundPlugin, chartAreaBackgroundPlugin, zeroLinePlugin];
  currentYearLabel: string | null = null;
  dateEom: string | null = null;

  showChart = false;
  showDataMissing = false;
  loadingFailed = false;
  isLoading = false;

  // Sequence guard: requests are not unsubscribed; only the latest one is allowed
  // to mutate state. Prevents a slow earlier response from overwriting a newer one.
  private requestSeq = 0;

  constructor(private apiService: GeneralInnovationActivityResultsService) {}

  get currentMetricFootnote(): string {
    return METRIC_OPTIONS[this.selectedMetric].footnote;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityid'] || changes['cluster_id'] || changes['cluster_name'] || changes['query'] || changes['theme_search_type']) {
      this.loadData();
    }
  }

  setMetric(metric: IThematicInnovationMetricKey): void {
    if (this.selectedMetric === metric) return;
    this.selectedMetric = metric;
    this.loadData();
  }

  resetZoom(): void {
    this.chartDirective?.chart?.resetZoom();
  }

  async downloadPng(): Promise<void> {
    const node = this.exportTarget?.nativeElement;
    if (!node) return;
    const canvas = await html2canvas(node, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
    });
    const safe = `${this.entity_name || 'company'}-${this.theme_label || 'theme'}`.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${safe}-innovation-activity.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private loadData(): void {
    if (!this.entityid || !this.tabId) return;

    const seq = ++this.requestSeq;
    this.isLoading = true;
    this.loadingFailed = false;
    this.showDataMissing = false;

    this.apiService.getInnovationActivityByCompany({
      entityid: this.entityid,
      theme_search_type: this.theme_search_type,
      cluster_id: this.cluster_id ?? null,
      cluster_name: this.cluster_name ?? null,
      query: this.query ?? null,
      stat_term: [this.selectedMetric],
      tabId: this.tabId,
    }).subscribe({
      next: (res) => {
        if (seq !== this.requestSeq) return;
        const payload = res.body?.payload;
        const data = payload?.data ?? [];
        if (!res.ok || !data.length) {
          this.isLoading = false;
          this.showChart = false;
          this.showDataMissing = true;
          return;
        }
        this.data = data;
        this.dateEom = payload?.date_eom ?? null;
        this.buildChart();
        this.isLoading = false;
      },
      error: () => {
        if (seq !== this.requestSeq) return;
        this.isLoading = false;
        this.showChart = false;
        this.loadingFailed = true;
      },
    });
  }

  private buildChart(): void {
    const cfg = METRIC_OPTIONS[this.selectedMetric];
    const sorted = [...this.data]
      .filter((p) => p.date_publication)
      .sort((a, b) => a.date_publication.localeCompare(b.date_publication));

    if (!sorted.length) {
      this.showDataMissing = true;
      this.showChart = false;
      return;
    }

    const todayYear = new Date().getUTCFullYear();
    const lastIdx = sorted.length - 1;
    const lastYear = new Date(sorted[lastIdx].date_publication).getUTCFullYear();
    const lastIsPartial = lastYear === todayYear;

    const partialLabel = this.dateEom ? `${formatMonthYear(this.dateEom)}*` : null;
    const labels = sorted.map((p, i) => {
      const y = String(new Date(p.date_publication).getUTCFullYear());
      if (lastIsPartial && i === lastIdx) return partialLabel ?? `${y}*`;
      return y;
    });
    const values = sorted.map((p) => (p[this.selectedMetric] as number | null) ?? null);
    const numericY = values.filter((v): v is number => typeof v === 'number');

    const barCount = Math.max(sorted.length, 1);
    const dynamicBarGap = Math.min(0.75, Math.max(0.35, 0.82 - barCount * 0.035));

    this.currentYearLabel = lastIsPartial ? labels[lastIdx] : null;
    const baseAlpha = cfg.opacity ?? 0.9;
    const barColors = labels.map((_, i) =>
      this.hexWithAlpha(cfg.color, lastIsPartial && i === lastIdx ? baseAlpha * 0.4 : baseAlpha),
    );

    this.chartjsData = {
      labels,
      datasets: [{
        label: cfg.label,
        data: values,
        backgroundColor: barColors,
        borderColor: cfg.color,
        borderWidth: 0,
        borderRadius: 4,
        borderSkipped: false,
        categoryPercentage: 0.85,
        barPercentage: Math.max(0.25, 1 - dynamicBarGap),
      }] as any,
    };

    const isGrowth = this.selectedMetric === GROWTH_METRIC;
    const yScale: any = {
      position: 'left',
      title: {
        display: true,
        text: cfg.yTitle,
        font: { family: FONT_FAMILY, size: 14, weight: 500 },
        color: COLOR.axisTitle,
        padding: { bottom: 8 },
      },
      ticks: {
        maxTicksLimit: 6,
        font: { family: FONT_FAMILY, size: 14 },
        color: COLOR.axisText,
        padding: 8,
      },
      grid: { color: COLOR.grid },
      border: { display: false },
    };

    if (isGrowth && numericY.length) {
      const maxAbs = Math.max(...numericY.map((v) => Math.abs(v)));
      const paddedAbs = Math.max(10, Math.ceil(maxAbs * 1.15));
      yScale.min = -paddedAbs;
      yScale.max = paddedAbs;
    }

    this.chartjsOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 24, right: 32, bottom: 8, left: 8 } },
      animation: { duration: 900, easing: 'easeOutQuart' },
      animations: {
        y: {
          type: 'number',
          duration: 900,
          easing: 'easeOutQuart',
          from: (ctx: any) => {
            if (ctx.type !== 'data' || ctx.mode !== 'default') return undefined;
            return ctx.chart.scales['y']?.getPixelForValue(0);
          },
        },
        x: { duration: 0 },
      },
      transitions: {
        active: { animation: { duration: 0 } },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: COLOR.tooltipBg,
          titleColor: COLOR.tooltipTitle,
          titleFont: { family: FONT_FAMILY, size: 13, weight: 500 },
          bodyColor: COLOR.tooltipBody,
          bodyFont: { family: FONT_FAMILY, size: 15, weight: 600 },
          padding: 12,
          borderColor: COLOR.tooltipBorder,
          borderWidth: 1,
          displayColors: false,
          cornerRadius: 8,
          caretSize: 0,
          callbacks: {
            title: (items: any[]) => items[0]?.label ?? '',
            label: (item: any) => {
              const v = item.parsed.y;
              if (v == null) return '';
              return `${v.toFixed(cfg.decimals ?? 0)}${cfg.hoverSuffix ?? ''}`;
            },
          },
        },
        companyThemeActivityZeroLine: { enabled: isGrowth },
        zoom: {
          pan: { enabled: true, mode: 'x', modifierKey: 'shift' },
          zoom: {
            wheel: { enabled: false },
            pinch: { enabled: true },
            drag: {
              enabled: true,
              backgroundColor: 'rgba(99, 102, 241, 0.15)',
              borderColor: 'rgba(99, 102, 241, 0.6)',
              borderWidth: 1,
            },
            mode: 'x',
          },
        },
      } as any,
      scales: {
        x: {
          type: 'category',
          ticks: {
            maxRotation: 0,
            font: { family: FONT_FAMILY, size: 14 },
            color: COLOR.axisText,
            padding: 8,
          },
          grid: { display: false },
          border: { display: false },
        },
        y: yScale,
      },
    };

    this.showChart = true;
    this.showDataMissing = false;
    this.loadingFailed = false;
  }

  private hexWithAlpha(hex: string, alpha: number): string {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
